/**
 * Prompt construction for each feature. Pure functions, no IO, easy to test.
 */
import type { LlmMessage, ToneStyle } from "@/types";

export interface DraftInput {
  /** The email being replied to (body). Empty for a new draft. */
  incoming: string;
  /** User's free-form instructions, e.g. "decline the meeting politely". */
  instruction: string;
  headers: { subject: string; from: string; to: string };
}

export function buildDraftPrompt(i: DraftInput): LlmMessage[] {
  const context = i.incoming
    ? `You are replying to this email.\n\nFrom: ${i.headers.from}\nSubject: ${i.headers.subject}\n\n--- BEGIN INCOMING EMAIL ---\n${i.incoming}\n--- END INCOMING EMAIL ---`
    : `You are composing a new email.\nSubject: ${i.headers.subject}`;

  return [
    {
      role: "system",
      content:
        "You are an email-writing assistant. Write a complete, ready-to-send email reply or new message based on the user's instruction. Do not include a subject line. Do not add commentary. Match the language of the incoming email unless told otherwise.",
    },
    {
      role: "user",
      content: `${context}\n\nInstruction: ${i.instruction || "(no specific instruction — write a sensible reply)"}`,
    },
  ];
}

export function buildTranslatePrompt(text: string, targetLang: string): LlmMessage[] {
  return [
    {
      role: "system",
      content:
        "You are a professional translator. Translate the given text to the target language. Preserve formatting, greetings, and tone. Output only the translation, no notes or back-translation.",
    },
    { role: "user", content: `Translate to ${targetLang}:\n\n${text}` },
  ];
}

export function buildSummarizePrompt(thread: string): LlmMessage[] {
  return [
    {
      role: "system",
      content:
        "You summarize email threads concisely. Produce: (1) a one-line summary of the latest state, (2) up to 5 bullet points of key decisions/asks, (3) any open questions. Be terse.",
    },
    { role: "user", content: `Summarize this email thread:\n\n${thread}` },
  ];
}

export function buildTonePrompt(text: string, tone: ToneStyle): LlmMessage[] {
  return [
    {
      role: "system",
      content:
        "You rewrite the given text to change its tone while preserving meaning, names, dates, and facts. Output only the rewritten text, no preamble.",
    },
    { role: "user", content: `Rewrite this to be ${tone}:\n\n${text}` },
  ];
}

export function buildProofreadPrompt(text: string): LlmMessage[] {
  return [
    {
      role: "system",
      content:
        "You are a meticulous proofreader. Fix grammar, spelling, punctuation, and obvious word-choice issues in the given text. Preserve the author's voice and formatting. Output only the corrected text.",
    },
    { role: "user", content: `Proofread:\n\n${text}` },
  ];
}
